import React from 'react'
import Table, { TableRow, TableRowCell } from 'core-system/Table'
import Text from 'core-system/Text'
import Chip from 'core-system/Chip'
import { ScheduledReportRun } from 'redux/automatedReporting/automatedReportingTypes'
import {
  formatReportType,
  formatScheduledReportRunDataWindow,
  formatScheduledReportRunDate,
  formatScheduledReportRunDateTime,
  formatScheduledReportRunRecipients,
  formatScheduledReportRunSegments,
  formatScheduledReportRunStatus,
  getScheduledReportRunStatusChipVariant,
} from '../utils'

interface ScheduledReportRunsTableProps {
  scheduledReportRuns: ScheduledReportRun[]
  isLoading: boolean
  error: string | null
}

const HeaderCell = ({ text }: { text: string }) => (
  <TableRowCell noWrap={true}>
    <Text variant='action4'>{text}</Text>
  </TableRowCell>
)

const RowCell = ({
  children,
  noWrap = true,
}: {
  children: React.ReactNode
  noWrap?: boolean
}) => (
  <TableRowCell style={{ padding: '1.425rem' }} noWrap={noWrap}>
    <Text fontWeight={300} variant='action3'>
      {children}
    </Text>
  </TableRowCell>
)

const ScheduledReportRunsTable = React.memo(
  ({
    scheduledReportRuns,
    isLoading,
    error,
  }: ScheduledReportRunsTableProps) => {
    const emptyMessage = error
      ? 'Unable to load scheduled report history.'
      : 'No scheduled report history found.'

    return (
      <Table
        headerCells={[
          <HeaderCell text='Report' />,
          <HeaderCell text='Type' />,
          <HeaderCell text='Scheduled For' />,
          <HeaderCell text='Data Window' />,
          <HeaderCell text='Started' />,
          <HeaderCell text='Completed' />,
          <HeaderCell text='Status' />,
          <HeaderCell text='Rows' />,
          <HeaderCell text='Recipients' />,
          <HeaderCell text='Segments' />,
          <HeaderCell text='Error' />,
        ]}
        rows={scheduledReportRuns.map((scheduledReportRun) => (
          <TableRow key={scheduledReportRun.id}>
            <RowCell>{scheduledReportRun.reportName}</RowCell>
            <RowCell>{formatReportType(scheduledReportRun.reportType)}</RowCell>
            <RowCell>
              {formatScheduledReportRunDate(scheduledReportRun.scheduledFor)}
            </RowCell>
            <RowCell>
              {formatScheduledReportRunDataWindow(scheduledReportRun)}
            </RowCell>
            <RowCell>
              {formatScheduledReportRunDateTime(scheduledReportRun.startedAt)}
            </RowCell>
            <RowCell>
              {formatScheduledReportRunDateTime(scheduledReportRun.completedAt)}
            </RowCell>
            <RowCell>
              <Chip
                variant={getScheduledReportRunStatusChipVariant(
                  scheduledReportRun.status
                )}
              >
                {formatScheduledReportRunStatus(scheduledReportRun.status)}
              </Chip>
            </RowCell>
            <RowCell>{scheduledReportRun.rowCount}</RowCell>
            <RowCell noWrap={false}>
              {formatScheduledReportRunRecipients(scheduledReportRun)}
            </RowCell>
            <RowCell noWrap={false}>
              {formatScheduledReportRunSegments(scheduledReportRun)}
            </RowCell>
            <RowCell noWrap={false}>
              {scheduledReportRun.errorMessage || '-'}
            </RowCell>
          </TableRow>
        ))}
        maxRows={10}
        emptyMessage={emptyMessage}
        isLoading={isLoading}
        showPagination={scheduledReportRuns.length > 10}
      />
    )
  }
)

if (process.env.NODE_ENV !== 'production') {
  ScheduledReportRunsTable.displayName = 'ScheduledReportRunsTable'
}

export default ScheduledReportRunsTable
